import { ArrowLeft, MessageSquare, Settings as SettingsIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api';
import { t } from '../../i18n';
import type { ChildDetail as ChildDetailType, FeedItem, PrayerTime } from '../../types';
import { AppShell } from '../shared/AppShell';
import { Avatar } from '../shared/Avatar';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
import { ProgressTrack } from '../shared/ProgressTrack';
import { StatusCircle } from '../shared/StatusCircle';
import { FeedRow, useFeedPlayer } from './FeedRow';
import { MessageComposer } from './MessageComposer';

const PRAYER_NAME_KEY: Record<PrayerTime, string> = {
  bomdod: 'prayer.bomdod',
  peshin: 'prayer.peshin',
  asr: 'prayer.asr',
  shom: 'prayer.shom',
  xufton: 'prayer.xufton',
};

export function ChildDetailScreen() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const childId = Number(id);
  const [detail, setDetail] = useState<ChildDetailType | null>(null);
  const [history, setHistory] = useState<FeedItem[]>([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const player = useFeedPlayer(history, setHistory);

  useEffect(() => {
    if (!Number.isFinite(childId)) return;
    api.childDetail(childId).then(setDetail);
    api.parentFeed({ child_id: childId, limit: 50 }).then((r) => setHistory(r.items));
  }, [childId]);

  if (!detail) {
    return (
      <AppShell>
        <main className="mx-auto flex min-h-dvh max-w-2xl flex-col p-5">
          <p className="text-[13px] text-ink-muted">{t('common.loading')}</p>
        </main>
      </AppShell>
    );
  }

  const c = detail.child;

  return (
    <AppShell ramp={c.color_ramp}>
      <main className="mx-auto flex min-h-dvh max-w-2xl flex-col gap-5 p-5 pb-12">
        <header className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label={t('common.back')} className="text-ink-muted">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[18px] font-medium">{c.name}</h1>
        </header>

        <Card>
          <div className="flex items-center gap-4">
            <div className={`ramp-${c.color_ramp}`}>
              <Avatar
                initial={c.avatar_initial}
                ramp={c.color_ramp}
                size="lg"
                src={c.avatar_url ?? undefined}
              />
            </div>
            <div className="flex flex-1 flex-col">
              <span className="text-[18px] font-medium">{c.name}</span>
              <span className="text-[12px] text-ink-muted">{c.age} yosh · {c.streak_days} kun silsila</span>
              <span className="mt-1 text-[12px] text-ink-muted">
                {c.current_surah_number}. {c.current_surah_name_uz}
              </span>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button onClick={() => setComposerOpen(true)} fullWidth>
              <MessageSquare size={16} className="mr-2" />
              {t('parent.message.send')}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/parent/settings')} fullWidth>
              <SettingsIcon size={16} className="mr-2" />
              {t('common.settings')}
            </Button>
          </div>
        </Card>

        <section className="flex flex-col gap-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">Bugun</div>
          <Card>
            <ul className="flex flex-col">
              {detail.today.prayers.map((p) => (
                <li key={p.prayer_time} className="flex items-center gap-3 py-1.5">
                  <StatusCircle status={p.status} isOptional={p.is_optional} />
                  <span className="flex-1 text-[14px]">{t(PRAYER_NAME_KEY[p.prayer_time])}</span>
                  <ProgressTrack
                    target={c.target_per_prayer}
                    done={p.recitation_count}
                    status={p.status}
                  />
                </li>
              ))}
            </ul>
          </Card>
        </section>

        <section className="flex flex-col gap-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">Statistika</div>
          <Card>
            <div className="grid grid-cols-3 gap-3 text-center">
              <Stat label="Tugatilgan suralar" value={detail.stats.surahs_completed.length} />
              <Stat label="Jami yozuv" value={detail.stats.total_recitations} />
              <Stat
                label="Jami davomiyligi"
                value={`${Math.floor(detail.stats.total_duration_seconds / 60)} daq`}
              />
            </div>
            {detail.stats.streak_history.length > 0 ? (
              <div className="mt-4 text-[12px] text-ink-muted">
                Silsila tarixchasi: {detail.stats.streak_history.length} ta davr,
                eng uzun: {Math.max(...detail.stats.streak_history.map((s) => s.length))} kun
              </div>
            ) : null}
          </Card>
        </section>

        <section className="flex flex-col gap-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">Yozuvlar</div>
          {history.length === 0 ? (
            <p className="text-[13px] text-ink-muted">{t('parent.feed.empty')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {history.map((item) => (
                <FeedRow
                  key={item.key}
                  item={item}
                  player={player}
                  onDeleted={(key) => setHistory((prev) => prev.filter((it) => it.key !== key))}
                />
              ))}
            </ul>
          )}
        </section>
      </main>

      <MessageComposer
        open={composerOpen}
        childId={c.id}
        childName={c.name}
        onClose={() => setComposerOpen(false)}
      />
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-[20px] font-medium tabular-nums">{value}</div>
      <div className="text-[11px] text-ink-subtle">{label}</div>
    </div>
  );
}
